import React, { useState, useEffect } from 'react';
import { Breadcrumbs } from '../../../../AbstractElements';
import { Card, CardBody, Col, Container, Row, Form, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import {
    createAccidentFileFromExpert,
    getCallFormById,
    getCustomerById,
    getDataPoolById,
    getLicensePlateById,
} from '../../../../Api/ApiMethods';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import CustomerInfoForm from './CustomerInfoForm';
import ContactInfoForm from './ContactInfoForm';
import PowerOfAttorneyForm from './PowerOfAttorneyForm';
import AddressInfoForm from './AddressInfoForm';
import CallFormHeader from './CallFormHeader';
import CallFormFooter from './CallFormFooter';
import AccidentInfoForm from './AccidentInfoForm';
import DocumentRequestForm from './DocumentRequestForm';
import CommentsComponent, { callFormStateMappings } from '../Component/CommentsComponent';
import FilesComponent from '../Component/FilesComponent';
import ExpenseCardList from '../Component/ExpenseCardList';
import AccidentFileExpenseList from '../Component/AccidentFileExpenseList';
import ExpertForm from './ExpertForm';
import ExpertReportForm from './ExpertReportForm';
import InsuranceCompanyReportForm from './InsuranceCompanyReportForm';
import useCallFormWorkflow from './hooks/useCallFormWorkflow';
import useCallFormLookups from './hooks/useCallFormLookups';
import { buildCallFormPayload, prepareUpdatedComments, submitCallFormRequest } from './utils/callFormSubmission';
import { extractEntityData, mapDataPoolToFormData, mapExistingFormData, mergeLicensePlateAndCustomerData } from './utils/callFormHydrationMappers';
import { getCallFormSectionVisibility } from './utils/callFormSectionVisibility';
import { validateCallFormRequiredFields } from './utils/callFormValidation';
import { normalizeFieldValue, hasFieldConfig } from '../../../../_helper/regexHandler';
import { shouldToastError } from '../../../../Api/utils';

const API_BASE_URL = process.env.REACT_APP_LACIVERT_API_ENDPOINT;
const API_ENDPOINTS = {
    insuranceCompany: `${API_BASE_URL}/insurancecompany`,
    city: `${API_BASE_URL}/city`,
    district: `${API_BASE_URL}/district`,
    workFileType: `${API_BASE_URL}/workfiletype`,
};

const CallForm = () => {
    // Çağrı formu sayfasının ana veri ve akış yönetimi
    const navigate = useNavigate();
    const location = useLocation();
    const isNewFormFromDataPool = location.pathname.includes('/new-callform/');
    const insuranceCompanyApiUrl = API_ENDPOINTS.insuranceCompany;
    const cityApiUrl = API_ENDPOINTS.city;
    const districtApiUrl = API_ENDPOINTS.district;
    const workFileTypeApiUrl = API_ENDPOINTS.workFileType;

    const { id } = useParams();

    const [canEdit, setCanEdit] = useState(id ? null : true);
    const [isLocked, setIsLocked] = useState(false);
    const [, setLoading] = useState(false);
    const [creatingAccidentFileIndex, setCreatingAccidentFileIndex] = useState(null);

    const [errors, setErrors] = useState({});
    const [existingComments, setExistingComments] = useState([]);

    const {
        currentFormState,
        setCurrentFormState,
        fieldPermissions,
        setFieldPermissions,
        dynamicCallFormStates,
        dynamicStateTransitions,
        loadingStates,
        fetchFieldPermissions,
    } = useCallFormWorkflow({
        id,
    });

    const pageTitle = id ? 'Çağrı Formu' : 'Yeni Çağrı Formu';

    const [formData, setFormData] = useState({
        recipientOfPowerOfAttorney: null,
        agentId: null,
        registrationDate: null,
        clientName: null,
        companyName: null,
        tcid: null,
        crsNumber: null,
        taxNumber: null,
        isThereLovClaim: null,
        hasCarSoldBefore: null,
        isTrialDocument: null,
        isWorkDocument: null,
        bonusEligibility: null,
        shouldPayNotaryFee: null,
        notaryFeeAmount: null,
        licensePlate: null,
        chassisNumber: null,
        carBrand: null,
        carModel: null,
        carYear: null,
        estimatedDepreciationAmount: null,
        damageAmount: null,
        damageDifferenceAmount: null,
        lorAmount: null,
        accidentDate: null,
        faultRate: null,
        phoneNumber: null,
        phoneNumber2: null,
        companyRepresentative: null,
        email: null,
        email2: null,
        iban: null,
        iban2: null,
        middleman: null,
        middlemanIban: null,

        customerContractRate: null,
        customerEnforcementAttorneyFeeShareRate: null,
        customerJudgmentAttorneyFeeShareRate: null,
        customerPayInterestToCustomer: null,
        customerIsMiddlemanContractBased: false,
        customerMiddlemanContractRate: null,
        customerMiddlemanContractPrice: null,

        isPowerOfAttorneyTimed: null,
        powerOfAttorneyDate: null,
        powerOfAttorneyEndDate: null,
        powerOfAttorneyNumber: null,
        notaryNumber: null,
        isLicensePlatesLimited: null,
        plateLimit: [],
        isAccidentDateLimited: null,
        plateLimitAccidentDate: null,
        hasOriginalArrived: null,
        hasContractArrived: null,
        reference: null,
        city: null,
        district: null,
        address: null,
        notes: null,
        customerType: 'Bireysel',

        licensePlateCustomerContractRate: null,
        licensePlateEnforcementAttorneyFeeShareRate: null,
        licensePlateJudgmentAttorneyFeeShareRate: null,
        licensePlatePayInterestToCustomer: null,
        licensePlateIsMiddlemanContractBased: false,
        licensePlateMiddlemanContractRate: null,
        licensePlateMiddlemanContractPrice: null,

        isThereOpenDamageFile: null,
        jobToBeWorked: null,
        whoseInsurance: null,
        documentRequestDate: null,
        documentRequestStatus: null,
        documentReceivedDate: null,

        filesToBeWorked: [],

        trafficInsurancePolicy: {
            trafficInsuranceProvider: [],
            trafficInsuranceNumber: null,
            trafficInsuranceExpiry: null,
            trafficInsuranceStart: null,
        },

        cascoInsurancePolicy: {
            cascoInsuranceProvider: [],
            cascoInsuranceNumber: null,
            cascoInsuranceExpiry: null,
            cascoInsuranceStart: null,
        },

        counterpartyType: 'Bireysel',
        counterpartyIdentificationNumber: null,
        counterpartyTaxNumber: null,
        counterpartyCarOwner: null,
        counterpartyLicensePlateNumber: null,
        counterpartyAddress: null,

        counterpartyTrafficInsurancePolicy: {
            trafficInsuranceProvider: [],
            trafficInsuranceNumber: null,
            trafficInsuranceExpiry: null,
            trafficInsuranceStart: null,
        },

        counterpartyCascoInsurancePolicy: {
            cascoInsuranceProvider: [],
            cascoInsuranceNumber: null,
            cascoInsuranceExpiry: null,
            cascoInsuranceStart: null,
        },

        ourFaultRate: null,
        counterpartyFaultRate: null,

        willLorBeWorked: "no",
        expertReportFiles: {},
        insuranceApplicationFiles: {},
    }); 

    const {
        insuranceCompanies,
        workFileTypes,
        cities,
        filteredDistricts,
    } = useCallFormLookups({
        insuranceCompanyApiUrl,
        workFileTypeApiUrl,
        cityApiUrl,
        districtApiUrl,
        cityId: formData.city,
    });
    
    const [accidentFileId, setAccidentFileId] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [powerOfAttorneyId, setPowerOfAttorneyId] = useState(null);
    const [accidentFiles, setAccidentFiles] = useState([]);

    const handleCityChange = (cityId) => {
        // Şehir değiştiğinde ilçe seçimini sıfırlar
        setFormData(prev => ({
            ...prev,
            city: cityId || null,
            district: null
        }));
    };

    // Plaka detayından gelinen yeni formda müşteri/plaka verisini ön doldurur
    useEffect(() => {
        if (location.state?.fromLicensePlate && location.state?.customerId && location.state?.licensePlateId && !id) {
            const fetchLicensePlateAndCustomerData = async () => {
                try {
                    const licensePlateResponse = await getLicensePlateById(location.state.licensePlateId);
                    const customerResponse = await getCustomerById(location.state.customerId);

                    const licensePlateData = extractEntityData(licensePlateResponse);
                    const customerData = extractEntityData(customerResponse);

                    if (licensePlateData || customerData) {
                        setFormData((prev) =>
                            mergeLicensePlateAndCustomerData({
                                prevFormData: prev,
                                customerData,
                                licensePlateData,
                                fallbackCustomerId: location.state.customerId,
                            })
                        );
                        setCustomerId(location.state.customerId);
                    } else {
                        toast.error('Plaka veya müşteri bilgileri alınamadı.');
                    }

                } catch (error) {
                    if (shouldToastError(error)) {
                        toast.error('Veriler yüklenirken hata oluştu.');
                    }
                }
            };

            fetchLicensePlateAndCustomerData();

            window.history.replaceState({}, document.title);
        }
    }, [location.state, id]);

    // Düzenleme modunda çağrı formu veya datapool verisini yükler
    useEffect(() => {
        if (id) {
            if (isNewFormFromDataPool) {
                const fetchDataPoolData = async () => {
                    try {
                        const response = await getDataPoolById(id);

                        if (response) {
                            populateFormWithDataPoolData(response);
                        }
                    } catch (error) {
                        if (shouldToastError(error)) {
                            toast.error('Datapool veri çekme hatası!');
                        }
                    }
                };

                fetchDataPoolData();
            } else {
                const fetchExistingFormData = async () => {
                    try {
                        const response = await getCallFormById(id);

                        if (response && response.isSuccess) {
                            const apiData = response.data;

                            const locked = apiData.isLocked === true;
                            setIsLocked(locked);
                            setCanEdit(!locked && apiData.canEdit !== false);

                            if (apiData.accidentFiles && apiData.accidentFiles.length > 0) {
                                setAccidentFileId(apiData.accidentFiles[0].id);
                            }

                            if (apiData.customers && apiData.customers.length > 0) {
                                setCustomerId(apiData.customers[0].id);
                            } else if (apiData.customer && apiData.customer.length > 0) {
                                setCustomerId(apiData.customer[0]);
                            }

                            if (apiData.powerOfAttorneys && apiData.powerOfAttorneys.length > 0) {
                                setPowerOfAttorneyId(apiData.powerOfAttorneys[0].id);
                            }

                            const customer = apiData.customers && apiData.customers.length > 0
                                ? apiData.customers[0]
                                : null;

                            const licensePlate = apiData.licensePlates && apiData.licensePlates.length > 0
                                ? apiData.licensePlates[0]
                                : null;

                            const accident = apiData.accidents && apiData.accidents.length > 0
                                ? apiData.accidents[0]
                                : null;
                            const accidentFiles = apiData.accidentFiles || [];
                            setAccidentFiles(accidentFiles);
                            const accidentFile = accidentFiles.length > 0 ? accidentFiles[0] : null;

                            const allFilesToBeWorked = accidentFiles.map((af) => {
                                const filesToBeWorked = af.filesToBeWorked || [];

                                const processedFiles = filesToBeWorked.map(file => ({
                                    fileType: file.fileType ? file.fileType.map(ft => ft.id) : [],
                                    _fileTypeTitles: file.fileType ? file.fileType.map(ft => ft.title) : [],
                                    fileExpense: file.fileExpense || 0,
                                    numberOfDays: file.numberOfDays || 0,
                                    expensePerDay: file.expensePerDay || 0
                                }));

                                return {
                                    filesToBeWorked: processedFiles,
                                    withReport: af.withReport || false,
                                    withFault: af.withFault || false,
                                    withoutReport: af.withoutReport || false,
                                    accidentFileId: af.id,
                                    fileNumber: af.fileNumber,
                                    insuranceApplicationType: af.insuranceApplicationType || null,
                                    insuranceApplicationDate: af.insuranceApplicationDate || null,
                                    readyForArbitration: af.readyForArbitration || false
                                };
                            });

                            const powerOfAttorney = apiData.powerOfAttorneys && apiData.powerOfAttorneys.length > 0
                                ? apiData.powerOfAttorneys[0]
                                : null;

                            const comments = apiData.comments || [];
                            setExistingComments(comments);

                            const districtId = customer?.district?.id || null;
                            const cityId = customer?.district?.city?.id || null;

                            let determinedStateId = null;
                            let determinedStateName = 'Tüm Form';

                            if (apiData.callFormState && typeof apiData.callFormState === 'object' && apiData.callFormState.id) {
                                determinedStateId = apiData.callFormState.id;
                                determinedStateName = apiData.callFormState.title || 'Durum Başlığı Yok';
                            } else {
                                if (dynamicCallFormStates && dynamicCallFormStates['Tüm Form']) {
                                    determinedStateId = dynamicCallFormStates['Tüm Form'];
                                } else {
                                    determinedStateId = null;
                                }
                                determinedStateName = 'Tüm Form';
                            }

                            if (apiData.canEdit !== false) {
                                if (!determinedStateId && loadingStates) {
                                } else {
                                    await fetchFieldPermissions(determinedStateId);
                                }
                            } else {
                                setFieldPermissions([]);
                            }

                            setCurrentFormState(determinedStateName);

                            const formattedData = mapExistingFormData({
                                customer,
                                licensePlate,
                                accident,
                                powerOfAttorney,
                                accidentFile,
                                accidentFiles,
                                allFilesToBeWorked,
                                apiData,
                                cityId,
                                districtId,
                            });

                            setFormData(formattedData);


                        } else {
                            toast.error('Çağrı formu verileri alınamadı.');
                        }
                    } catch (error) {
                        if (shouldToastError(error)) {
                            toast.error('Çağrı formu verileri yüklenirken hata oluştu.');
                        }
                    }
                };

                fetchExistingFormData();
            }
        }
    }, [id, isNewFormFromDataPool]);

    // Datapool verisini form yapısına mapleyerek başlangıç durumunu ayarlar
    const populateFormWithDataPoolData = (response) => {
        setCurrentFormState('Yeni Çağrı Formu');
        setIsLocked(false);

        const { mappedData, canEdit: mappedCanEdit } = mapDataPoolToFormData({
            prevFormData: formData,
            response,
        });

        setFormData(mappedData);
        setCanEdit(mappedCanEdit);

        if (dynamicCallFormStates && dynamicCallFormStates['Yeni Çağrı Formu']) {
            const stateId = dynamicCallFormStates['Yeni Çağrı Formu'];
            fetchFieldPermissions(stateId);
        }
    };

    // Mevcut duruma ve aksiyona göre bir sonraki durum kimliğini hesaplar
    const getNextState = (action) => {
        if (loadingStates || !dynamicStateTransitions || !dynamicCallFormStates) {
            return null;
        }

        if (action === 'addExpense') {
            const currentId = dynamicCallFormStates[currentFormState];
            return currentId || null;
        }

        const transitionsForCurrent = dynamicStateTransitions[currentFormState];
        const nextStateId = transitionsForCurrent?.[action];

        const currentStateId = dynamicCallFormStates[currentFormState];
        const resultId = nextStateId !== undefined ? nextStateId : currentStateId;
        return resultId;
    };

    // Formu doğrulayıp uygun durum geçişiyle API'ye gönderir
    const handleFormSubmission = async (action, additionalParams = {}) => {
        if (!validateForm(action)) {
            toast.error('Lütfen zorunlu alanları doldurun.');
            return;
        }
        if (loadingStates || !dynamicCallFormStates || !dynamicStateTransitions) {
            toast.warning('Durum bilgileri veya geçiş kuralları henüz yükleniyor, lütfen bekleyin.');
            return;
        }

        try {
            setLoading(true);
            const stateId = getNextState(action);

            if (stateId === undefined || stateId === null) {
                toast.error(`Bir sonraki durum ID'si belirlenemedi.`);
                return;
            }

            const currentDepartment = callFormStateMappings[currentFormState];
            const userFullName = localStorage.getItem('userFullName') || 'Kullanıcı';
            const updatedComments = prepareUpdatedComments({
                existingComments,
                currentDepartment,
                userFullName,
                notes: formData.notes,
            });

            const formDataToSend = buildCallFormPayload({
                formData,
                stateId,
                id,
                isNewFormFromDataPool,
                action,
                additionalParams,
                updatedComments,
                workFileTypes,
                insuranceCompanies,
            });

            await submitCallFormRequest({
                id,
                isNewFormFromDataPool,
                formDataToSend,
            });

            toast.success('İşlem başarıyla tamamlandı.');
            navigate('/todo-list');

        } catch (error) {
            if (shouldToastError(error)) {
                toast.error('İşlem sırasında genel bir hata oluştu.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Form alanlarını varsayılan değerlere sıfırlar
    const resetForm = () => {
        setFormData({
            recipientOfPowerOfAttorney: null,
            agentId: null,
            registrationDate: null,
            clientName: null,
            companyName: null,
            tcid: null,
            crsNumber: null,
            taxNumber: null,
            isThereLovClaim: null,
            hasCarSoldBefore: null,
            isTrialDocument: "no",
            isWorkDocument: "no",
            bonusEligibility: null,
            shouldPayNotaryFee: null,
            notaryFeeAmount: null,
            licensePlate: null,
            chassisNumber: null,
            carBrand: null,
            carModel: null,
            carYear: null,
            estimatedDepreciationAmount: null,
            damageAmount: null,
            damageDifferenceAmount: null,
            lorAmount: null,
            accidentDate: null,
            faultRate: null,
            phoneNumber: null,
            phoneNumber2: null,
            phoneDescription: null,
            phoneDescription2: null,
            companyRepresentative: null,
            email: null,
            email2: null,
            emailDescription: null,
            emailDescription2: null,
            iban: null,
            iban2: null,
            ibanDescription: null,
            ibanDescription2: null,
            middleman: null,
            middlemanIban: null,
            MiddlemanContractRate: null,
            isMiddlemanContractBased: false,
            middlemanContractPrice: null,
            isPowerOfAttorneyTimed: null,
            powerOfAttorneyDate: null,
            powerOfAttorneyEndDate: null,
            powerOfAttorneyNumber: null,
            notaryNumber: null,
            contractRate: null,
            isLicensePlatesLimited: null,
            plateLimit: [],
            isAccidentDateLimited: null,
            plateLimitAccidentDate: null,
            hasOriginalArrived: null,
            hasContractArrived: null,
            reference: null,
            city: null,
            district: null,
            address: null,
            notes: null,
            customerType: 'Bireysel',

            isThereOpenDamageFile: null,
            jobToBeWorked: null,
            whoseInsurance: null,
            documentRequestDate: null,
            documentRequestStatus: null,
            documentReceivedDate: null,

            filesToBeWorked: [],

            trafficInsurancePolicy: {
                trafficInsuranceProvider: [],
                trafficInsuranceNumber: null,
                trafficInsuranceExpiry: null,
                trafficInsuranceStart: null,
            },

            cascoInsurancePolicy: {
                cascoInsuranceProvider: [],
                cascoInsuranceNumber: null,
                cascoInsuranceExpiry: null,
                cascoInsuranceStart: null,
            },

            counterpartyType: 'Bireysel',
            counterpartyIdentificationNumber: null,
            counterpartyTaxNumber: null,
            counterpartyCarOwner: null,
            counterpartyLicensePlateNumber: null,
            counterpartyAddress: null,

            counterpartyTrafficInsurancePolicy: {
                trafficInsuranceProvider: [],
                trafficInsuranceNumber: null,
                trafficInsuranceExpiry: null,
                trafficInsuranceStart: null,
            },

            counterpartyCascoInsurancePolicy: {
                cascoInsuranceProvider: [],
                cascoInsuranceNumber: null,
                cascoInsuranceExpiry: null,
                cascoInsuranceStart: null,
            },

            ourFaultRate: null,
            counterpartyFaultRate: null,

            willLorBeWorked: "no",
            expertReportFiles: {},
            insuranceApplicationFiles: {},
        });
        setErrors({});
    };

    // Alan değişimlerini normalize eder ve bağlı sözleşme alanlarını eşitler
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        let processedValue = type === 'checkbox' ? checked : value;

        if (type !== 'checkbox' && typeof processedValue === 'string' && hasFieldConfig(name)) {
            processedValue = normalizeFieldValue(name, processedValue);
        }


        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: processedValue,
            };

            if (name === 'customerContractRate') {
                updatedFormData.licensePlateCustomerContractRate = processedValue;
            } else if (name === 'customerEnforcementAttorneyFeeShareRate') {
                updatedFormData.licensePlateEnforcementAttorneyFeeShareRate = processedValue;
            } else if (name === 'customerJudgmentAttorneyFeeShareRate') {
                updatedFormData.licensePlateJudgmentAttorneyFeeShareRate = processedValue;
            } else if (name === 'customerPayInterestToCustomer') {
                updatedFormData.licensePlatePayInterestToCustomer = processedValue;
            } else if (name === 'customerIsMiddlemanContractBased') {
                updatedFormData.licensePlateIsMiddlemanContractBased = processedValue;
                if (processedValue === true) {
                    updatedFormData.customerMiddlemanContractPrice = null;
                    updatedFormData.licensePlateMiddlemanContractPrice = null;
                } else {
                    updatedFormData.customerMiddlemanContractRate = null;
                    updatedFormData.licensePlateMiddlemanContractRate = null;
                }
            } else if (name === 'customerMiddlemanContractRate') {
                updatedFormData.licensePlateMiddlemanContractRate = processedValue;
            } else if (name === 'customerMiddlemanContractPrice') {
                updatedFormData.licensePlateMiddlemanContractPrice = processedValue;
            }

            return updatedFormData;
        });

        if (name === 'willLorBeWorked' && value === 'no') {
            const currentDepartment = callFormStateMappings[currentFormState];
            let updatedComments = [...existingComments];

            const existingCommentIndex = updatedComments.findIndex(
                comment => comment.commentType === currentDepartment
            );

            const lorNote = "Hak mahrumiyeti çalışılmayacak olarak işaretlendi.";

            if (existingCommentIndex >= 0) {
                const existingComment = updatedComments[existingCommentIndex].comment;
                if (!existingComment.includes(lorNote)) {
                    updatedComments[existingCommentIndex] = {
                        ...updatedComments[existingCommentIndex],
                        comment: existingComment ? `${existingComment}\n${lorNote}` : lorNote
                    };
                }
            } else {
                updatedComments.push({
                    commentType: currentDepartment,
                    comment: lorNote
                });
            }
            setExistingComments(updatedComments);
        }

        if (errors[name]) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: undefined,
            }));
        }
    };

    // Eksper bölümüne yeni çalışılacak dosya satırı ekler
    const handleAddWorkFile = (newFile) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            filesToBeWorked: [
                ...prevFormData.filesToBeWorked,
                newFile
            ]
        }));
    };

    // Eksper bölümündeki seçili dosya satırını kaldırır
    const handleRemoveWorkFile = (index) => {
        const updatedFiles = [...formData.filesToBeWorked];
        updatedFiles.splice(index, 1);
        setFormData((prevFormData) => ({
            ...prevFormData,
            filesToBeWorked: updatedFiles
        }));
    };

    // Seçili dosya girdisini API modeline çevirip kaza dosyası oluşturur
    const handleCreateAccidentFile = async (index) => {
        const fileToCreate = formData.filesToBeWorked[index];
        
        if (!fileToCreate) {
            toast.error('Dosya bulunamadı.');
            return;
        }

        if (!id) {
            toast.error('Çağrı formu ID bulunamadı.');
            return;
        }

        try {
            setCreatingAccidentFileIndex(index);

            const fileTypeAndExpense = [];

            if (fileToCreate.fileType && Array.isArray(fileToCreate.fileType)) {
                fileToCreate.fileType.forEach(typeId => {
                    const expense = fileToCreate.fileTypeExpenseDetails?.[typeId] || 0;

                    fileTypeAndExpense.push({
                        fileType: [typeId],
                        numberOfDays: fileToCreate.numberOfDays || 0,
                        expensePerDay: fileToCreate.expensePerDay || 0,
                        fileExpense: expense
                    });
                });
            }

            await createAccidentFileFromExpert({
                callFormId: id,
                fileTypeAndExpense,
                withReport: fileToCreate.withReport || false,
                withFault: fileToCreate.withFault || false,
                withoutReport: fileToCreate.withoutReport || false
            });

            setTimeout(() => window.location.reload(), 1000);
        } catch {
        } finally {
            setCreatingAccidentFileIndex(null);
        }
    };

    // Müşteri tipine göre bireysel/kurumsal alanları dengeler
    const handleCustomerTypeChange = (e) => {
        const newCustomerType = e.target.value;
        setFormData((prevFormData) => ({
            ...prevFormData,
            customerType: newCustomerType,
            clientName: newCustomerType === 'Bireysel' ? prevFormData.clientName : null,
            tcid: newCustomerType === 'Bireysel' ? prevFormData.tcid : null,
            companyName: newCustomerType === 'Kurumsal' ? prevFormData.companyName : null,
            crsNumber: newCustomerType === 'Kurumsal' ? prevFormData.crsNumber : null,
            taxNumber: newCustomerType === 'Kurumsal' ? prevFormData.taxNumber : null,
            lorAmount: newCustomerType === 'Kurumsal' ? prevFormData.lorAmount : null,
            companyRepresentative: newCustomerType === 'Kurumsal' ? prevFormData.companyRepresentative : null,
            email: newCustomerType === 'Kurumsal' ? prevFormData.email : null,
        }));
    };

    // Karşı taraf tipine göre kimlik/vergi alanlarını dengeler
    const handleCounterPartyTypeChange = (e) => {
        const newCounterPartyType = e.target.value;
        setFormData((prevFormData) => ({
            ...prevFormData,
            counterpartyType: newCounterPartyType,
            counterpartyIdentificationNumber: newCounterPartyType === 'Bireysel' ? prevFormData.counterpartyIdentificationNumber : null,
            counterpartyTaxNumber: newCounterPartyType === 'Kurumsal' ? prevFormData.counterpartyTaxNumber : null,
        }));
    };

    // Alan izinlerine göre zorunlu alan doğrulamasını çalıştırır
    const validateForm = (action) => {
        const newErrors = validateCallFormRequiredFields({
            formData,
            currentFormState,
            action,
            insuranceCompanies,
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    if (loadingStates) {
        return (
            <Container fluid>
                <Breadcrumbs mainTitle={pageTitle} parent='Sayfalar' title={pageTitle} />
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                <p>Durum bilgileri yükleniyor...</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    const sectionVisibility = getCallFormSectionVisibility(currentFormState);


    return (
        <Container fluid>
            <Breadcrumbs mainTitle={pageTitle} parent='Sayfalar' title={pageTitle} />
            {isLocked && (
                <Alert color="warning" className="mb-3">
                    Bu çağrı formu kilitli. Yalnızca görüntüleyebilirsiniz.
                </Alert>
            )}
            <Row>
                <Col sm="12">
                    <Card>
                        <CallFormHeader
                            formData={formData}
                            handleCustomerTypeChange={handleCustomerTypeChange}
                            resetForm={resetForm}
                            callFormState={currentFormState}
                            canEdit={canEdit}
                        />
                        <CardBody>
                            <Form>
                                {sectionVisibility.customerInfo && (
                                        <>
                                            <CustomerInfoForm
                                                callFormState={currentFormState}
                                                formData={formData}
                                                errors={errors}
                                                handleInputChange={handleInputChange}
                                                customerId={customerId}
                                                fieldPermissions={fieldPermissions}
                                                canEdit={canEdit}
                                            />
                                        </>
                                    )}

                                {sectionVisibility.contactInfo && (
                                        <>
                                            <div style={{ height: "20px" }}></div>
                                            <hr className="mt-4 mb-4" />
                                            <div style={{ height: "20px" }}></div>

                                            <ContactInfoForm
                                                formData={formData}
                                                errors={errors}
                                                handleInputChange={handleInputChange}
                                                setFormData={setFormData}
                                                fieldPermissions={fieldPermissions}
                                                canEdit={canEdit}
                                            />
                                        </>
                                    )}

                                {sectionVisibility.accidentInfo && (
                                        <>
                                            <div style={{ height: "20px" }}></div>
                                            <hr className="mt-4 mb-4" />
                                            <div style={{ height: "20px" }}></div>

                                            <AccidentInfoForm
                                                formData={formData}
                                                errors={errors}
                                                setFormData={setFormData}
                                                handleInputChange={handleInputChange}
                                                fieldPermissions={fieldPermissions}
                                                canEdit={canEdit}
                                            />
                                        </>
                                    )}

                                {sectionVisibility.powerOfAttorney && (
                                    <>
                                        <div style={{ height: "20px" }}></div>
                                        <hr className="mt-4 mb-4" />
                                        <div style={{ height: "20px" }}></div>

                                        <PowerOfAttorneyForm
                                            formData={formData}
                                            errors={errors}
                                            setFormData={setFormData}
                                            handleInputChange={handleInputChange}
                                            customerId={customerId}
                                            powerOfAttorneyId={powerOfAttorneyId}
                                            fieldPermissions={fieldPermissions}
                                            canEdit={canEdit}
                                        />
                                    </>
                                )}

                                {sectionVisibility.addressInfo && (
                                        <>
                                            <div style={{ height: "20px" }}></div>
                                            <hr className="mt-4 mb-4" />
                                            <div style={{ height: "20px" }}></div>

                                            <AddressInfoForm
                                                formData={formData}
                                                errors={errors}
                                                handleInputChange={handleInputChange}
                                                cities={cities}
                                                filteredDistricts={filteredDistricts}
                                                handleCityChange={handleCityChange}
                                                fieldPermissions={fieldPermissions}
                                                canEdit={canEdit}
                                            />
                                        </>
                                    )}

                                {sectionVisibility.documentRequest && (
                                    <>
                                        <div style={{ height: "20px" }}></div>
                                        <hr className="mt-4 mb-4" />
                                        <div style={{ height: "20px" }}></div>

                                        <DocumentRequestForm
                                            formData={formData}
                                            errors={errors}
                                            setFormData={setFormData}
                                            handleInputChange={handleInputChange}
                                            handleCounterPartyTypeChange={handleCounterPartyTypeChange}
                                            workFileTypes={workFileTypes}
                                            insuranceCompanies={insuranceCompanies}
                                            fieldPermissions={fieldPermissions}
                                            canEdit={canEdit}
                                        />
                                    </>
                                )}

                                {sectionVisibility.expert && (
                                        <>
                                            <div style={{ height: "20px" }}></div>
                                            <hr className="mt-4 mb-4" />
                                            <div style={{ height: "20px" }}></div>
                                            <ExpertForm
                                                formData={formData}
                                                errors={errors}
                                                setFormData={setFormData}
                                                handleInputChange={handleInputChange}
                                                handleAddWorkFile={handleAddWorkFile}
                                                handleRemoveWorkFile={handleRemoveWorkFile}
                                                handleCreateAccidentFile={handleCreateAccidentFile}
                                                workFileTypes={workFileTypes}
                                                insuranceCompanies={insuranceCompanies}
                                                callFormState={currentFormState}
                                                fieldPermissions={fieldPermissions}
                                                canEdit={canEdit}
                                                accidentFileId={accidentFileId}
                                                accidentFiles={accidentFiles}
                                                creatingAccidentFileIndex={creatingAccidentFileIndex}
                                                callFormId={id}
                                            />
                                        </>
                                    )}

                                {sectionVisibility.expertReport && (
                                    <>
                                        <div style={{ height: "20px" }}></div>
                                        <hr className="mt-4 mb-4" />
                                        <div style={{ height: "20px" }}></div>
                                        <ExpertReportForm
                                            formData={formData}
                                            errors={errors}
                                            setFormData={setFormData}
                                            workFileTypes={workFileTypes}
                                            callFormState={currentFormState}
                                            fieldPermissions={fieldPermissions}
                                            canEdit={canEdit}
                                        />
                                    </>
                                )}

                                {sectionVisibility.insuranceCompanyReport && (
                                    <>
                                        <div style={{ height: "20px" }}></div>
                                        <hr className="mt-4 mb-4" />
                                        <div style={{ height: "20px" }}></div>
                                        <InsuranceCompanyReportForm
                                            formData={formData}
                                            errors={errors}
                                            setFormData={setFormData}
                                            workFileTypes={workFileTypes}
                                            callFormState={currentFormState}
                                            fieldPermissions={fieldPermissions}
                                            canEdit={canEdit}
                                        />
                                    </>
                                )}

                                {sectionVisibility.expenseCardList && (
                                        <>
                                            <div style={{ height: "20px" }}></div>
                                            <hr className="mt-4 mb-4" />
                                            <div style={{ height: "20px" }}></div>
                                            <ExpenseCardList
                                                accidentFileId={accidentFileId}
                                                customerId={customerId}
                                                canEdit={canEdit}
                                                callformId={id}
                                                callFormState={currentFormState}
                                                initialCustomerContractRate={formData.licensePlateCustomerContractRate}
                                                initialIsMiddlemanContractBased={formData.licensePlateIsMiddlemanContractBased}
                                                initialMiddlemanContractRate={formData.licensePlateMiddlemanContractRate}
                                                initialMiddlemanContractPrice={formData.licensePlateMiddlemanContractPrice}
                                            />
                                        </>
                                    )}

                                {sectionVisibility.accidentFileExpenseList && (
                                    <>
                                        <div style={{ height: "20px" }}></div>
                                        <hr className="mt-4 mb-4" />
                                        <div style={{ height: "20px" }}></div>
                                        <AccidentFileExpenseList
                                            canEdit={canEdit}
                                            customerId={customerId}
                                            callformId={id}
                                            callFormState={currentFormState}
                                            formData={formData}
                                            workFileTypes={workFileTypes}
                                            initialCustomerContractRate={formData.licensePlateCustomerContractRate}
                                            initialIsMiddlemanContractBased={formData.licensePlateIsMiddlemanContractBased}
                                            initialMiddlemanContractRate={formData.licensePlateMiddlemanContractRate}
                                            initialMiddlemanContractPrice={formData.licensePlateMiddlemanContractPrice}
                                        />
                                    </>
                                )}

                                {/* 
                                {['Ödeme Bekleniyor',
                                    'Eksper Raporu Ödeme Bekleniyor',
                                    'Tüm Form'].includes(currentFormState) && accidentFileId && (
                                        <>
                                            <div style={{ height: "20px" }}></div>
                                            <hr className="mt-4 mb-4" />
                                            <div style={{ height: "20px" }}></div>

                                            <PaymentCollectionCardList
                                                accidentFileId={accidentFileId}
                                                canEdit={canEdit}
                                                customerId={customerId}
                                                callformId={id}
                                            />
                                        </>
                                    )}
                                */}

                                <div style={{ height: "20px" }}></div>
                                <hr className="mt-4 mb-4" />
                                <div style={{ height: "20px" }}></div>

                                <CommentsComponent
                                    formData={formData}
                                    errors={errors}
                                    handleInputChange={handleInputChange}
                                    callFormState={currentFormState}
                                    existingComments={existingComments}
                                    setExistingComments={setExistingComments}
                                    fieldPermissions={fieldPermissions}
                                    canEdit={canEdit}
                                />

                                <div style={{ height: "20px" }}></div>
                                <hr className="mt-4 mb-4" />
                                <div style={{ height: "20px" }}></div>

                                {!id && (
                                    <div className="alert alert-info mb-3">
                                        <i className="fa fa-info-circle me-2"></i>
                                        <strong>Bilgi:</strong> Dosya yükleyebilmek için önce çağrı formunu kaydetmeniz gerekiyor.
                                    </div>
                                )}

                                <div className="alert alert-warning mb-3">
                                    <i className="fa fa-exclamation-triangle me-2"></i>
                                    <strong>Not:</strong> <strong>VEKALETNAME</strong> belgeleri Vekalet Bilgileri bölümünden, <strong>SÖZLEŞME</strong> belgeleri ise Müşteri Bilgileri bölümünden yüklenmelidir.
                                </div>

                                {isNewFormFromDataPool && (
                                    <FilesComponent
                                        onFilesChange={() => {}}
                                        multiple={true}
                                        callFormReference={false}
                                        referenceId={id}
                                        typeFilter={['VEKALETNAME', 'SÖZLEŞME']}
                                        excludeTypes={true}
                                        title="Veri Havuzundan Gelen Belgeler"
                                        canEdit={false}
                                    />
                                )}

                                <FilesComponent
                                    onFilesChange={() => {}}
                                    multiple={true}
                                    callFormReference={true}
                                    referenceId={isNewFormFromDataPool ? null : id}
                                    typeFilter={['VEKALETNAME', 'SÖZLEŞME']}
                                    excludeTypes={true}
                                    title={isNewFormFromDataPool ? "Çağrı Formu Belgeleri" : "Belgeler"}
                                    canEdit={canEdit}
                                />
                            </Form>
                        </CardBody>

                        {!loadingStates && dynamicStateTransitions && canEdit && (
                            <CallFormFooter
                                callFormState={currentFormState}
                                handleOperation={handleFormSubmission}
                                accidentFileId={accidentFileId}
                                customerId={customerId}
                                callformId={id}
                                initialCustomerContractRate={formData.licensePlateCustomerContractRate}
                                initialIsMiddlemanContractBased={formData.licensePlateIsMiddlemanContractBased}
                                initialMiddlemanContractRate={formData.licensePlateMiddlemanContractRate}
                                initialMiddlemanContractPrice={formData.licensePlateMiddlemanContractPrice}
                                formData={formData}
                                isReapplicationFile={accidentFiles.some(af => af.isReapplicationFile === true)}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CallForm;
